import { ApiRoutes, QueryError } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import { Page, PageNode, PageNodeFormFields, PageNodesQueryKeys } from '@/modules/Pages';
import { Site } from '@/modules/Sites';

type PageNodeByIdQueryParams = Pick<PageNode, 'id' | 'slug' | 'path' | 'isHome' | 'extension'> & {
  site: Pick<Site, 'id'>;
  parentNode: Pick<PageNode, 'id' | 'slug'> | null;
  children: Pick<PageNode, 'id' | 'slug' | 'extension'>[];
  pages: Pick<Page, 'id' | 'usageFor' | 'title'>[];
  breadcrumb: PageNode['breadcrumb'];
}

export const useGetByIdPageNodeQuery = (siteId: string, nodeId: string) =>
  buildQuery<PageNodeByIdQueryParams, QueryError>([PageNodesQueryKeys.GetOneById, siteId, nodeId], async () => {
    const { data } = await apiExecutor.get<PageNodeByIdQueryParams>(
      ApiRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', nodeId)
    );
    return data;
  });

export const useGetRootPageNodeQuery = (siteId: string) =>
  buildQuery<PageNodeByIdQueryParams, QueryError>([PageNodesQueryKeys.GetOneById, siteId, 'root'], async () => {
    const { data } = await apiExecutor.get<PageNodeByIdQueryParams>(ApiRoutes.PageNodesRoot.replace(':siteId', siteId));
    return data;
  });

export const useEditPageNodeMutation = (id: string, siteId: string) =>
  buildMutation<PageNode, QueryError, PageNodeFormFields>([PageNodesQueryKeys.Edit, siteId, id], async (postData) => {
    const { data } = await apiExecutor.patch<PageNode>(
      ApiRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', id),
      { ...postData, id }
    );
    return data;
  });

export const useCreatePageNodeMutation = (siteId: string) =>
  buildMutation<PageNode, QueryError, PageNodeFormFields>([PageNodesQueryKeys.Create, siteId], async (postData) => {
    const { data } = await apiExecutor.post<PageNode>(ApiRoutes.PageNodes.replace(':siteId', siteId), {
      ...postData
    });
    return data;
  });

export const useDeletePageNodeMutation = (id: string, siteId: string) =>
  buildMutation<void, QueryError, void>([PageNodesQueryKeys.Delete, siteId, id], async () => {
    await apiExecutor.delete<void>(ApiRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', id));
  });

export const useAutocompletePageNodesQuery = (params: { query: string; includedIds?: string[]; }, siteId: string) =>
  buildQuery<Pick<PageNode, 'id' | 'path'>[], QueryError>([PageNodesQueryKeys.Autocomplete, siteId, params], async () => {
    const { data } = await apiExecutor.get<Pick<PageNode, 'id' | 'path'>[]>(
      ApiRoutes.PageNodesAutocomplete.replace(':siteId', siteId),
      { params: { q: params.query, includedIds: params?.includedIds?.length ? String(params.includedIds) : undefined, } }
    );
    return data;
  });
